import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { ReactiveFormsModule, FormsModule, FormGroup, FormBuilder, FormControl, Validators, AbstractControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogActions, MatDialogTitle, MatDialogContent, MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { Store } from '@ngxs/store';
import { MediaType } from '../../core/types';
import { Md5 } from 'ts-md5';
import { environment } from '../../../environments/environment';
import { ChooseMediumComponent } from '../../utils/component/choose-medium/choose-medium.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChooseMedium } from '../../utils/component/choose-medium/choose-medium.json.model';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ConfirmDialogComponent } from '../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { checkForNameErrors, getConsumerErrorText, getSubjectErrorText, MIN_NAME_LENGTH } from '../../core/util';
import { PixlEditorDialogComponent, PixlEditorDialogModel } from '../../pixl-editor/pixl-editor-dialog/pixl-editor-dialog.component';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import TurndownService from 'turndown';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { FunctionalHelpEntity, Medium } from '../functional-help.entity';
import { ConsumerEntity } from '../../consumer/consumer.entity';
import { Observable, from, switchMap } from 'rxjs';
import { ConsumerEntityService } from '../../consumer/services/consumer.entity.service';
import { normalizeOneRelation } from '../../core/decorators/relation.decorator';
import { WidgetMessageService } from '../../core/services/widget-message.service';
import { SubjectEntityService } from '../../subject/services/subject.entity.service';
import { SubjectEntity } from '../../subject/subject.entity';
import { TypeCBComponent } from '../../utils/component/type-filter-combobox/type-cb.component';
import { AssetEntity } from '../../asset/asset.entity';

@Component({
  selector: 'pixl-edit-functional-help',
  imports: [
    MatButtonModule,
    MatCheckboxModule,
    MatDialogActions,
    MatDialogTitle,
    MatDialogContent,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTabsModule,
    MatTooltipModule,
    ReactiveFormsModule,
    MatDividerModule,
    MatCardModule,
    MatIconModule,
    FormsModule,
    CommonModule,
    TranslocoModule,
    TypeCBComponent
  ],
  templateUrl: './edit-functional-help.component.html',
  styleUrl: './edit-functional-help.component.scss'
})
export class EditFunctionalHelpComponent implements OnInit, AfterViewInit {
  @ViewChild('desktopAudioCtrl') desktopAudioCtrl?: ElementRef;
  @ViewChild('mobileAudioCtrl') mobileAudioCtrl?: ElementRef;

  private fb = inject(FormBuilder);
  private dialogRef = inject<MatDialogRef<EditFunctionalHelpComponent>>(MatDialogRef);
  data? = inject<Partial<FunctionalHelpEntity>>(MAT_DIALOG_DATA);

  subjects$!: Observable<SubjectEntity[]>;
  consumers$!: Observable<ConsumerEntity[]>;
  currentPage = 1;
  pageSize = 20;

  private readonly store = inject(Store);
  private translocoService = inject(TranslocoService);
  private consumerService = inject(ConsumerEntityService);
  private subjectService = inject(SubjectEntityService);
  private widgetService = inject(WidgetMessageService);
  private destroyRef = inject(DestroyRef);
  public cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);

  public formGroup: FormGroup;

  public approval = signal(false);
  public assets: AssetEntity[] = [];
  public consumerError = signal(false);
  public subjectError = signal(false);
  public isNewRecord = signal(true);
  public consumerIsDeleted = signal(false);
  public competencyIsDeleted = signal(false);
  public key: string;

  // Injiziere den DomSanitizer
  private sanitizer = inject(DomSanitizer);
  private explanationContentHtml = '';
  private turndownService = new TurndownService();

  public desktopAudioUrl = signal('');
  public mobileAudioUrl = signal('');
  public imageUrl = signal('');
  public authToken: string | null = '';
  public assetsUrl: string = environment.apiUrl + '/assets';
  public mediaUrl: string = environment.assetsHost + '/';
  public viewContentHtml = signal<SafeHtml>('');

  constructor() {
    const exerciseKindValue =
      this.data?.exerciseSubKind ?
        `${this.data.exerciseKind}.${this.data.exerciseSubKind}` :
        this.data?.exerciseKind ?? '';
    this.formGroup = this.fb.group({
      name: new FormControl({ value: this.data?.name || '', disabled: this.approval() }, [Validators.required, Validators.minLength(MIN_NAME_LENGTH)]),
      subjectId: new FormControl({ value: this.data?.subjectId || undefined, disabled: this.approval() }),
      consumerId: new FormControl({ value: this.data?.consumerId || undefined, disabled: this.approval() }),
      exerciseKind: new FormControl({ value: exerciseKindValue || '', disabled: this.approval() }, Validators.required),
      exerciseSubKind: new FormControl({ value: this.data?.exerciseSubKind || '', disabled: this.approval() }),
      markdownText: new FormControl({ value: this.data?.markdownText || '', disabled: this.approval() }),
      markupText: new FormControl({ value: this.data?.markupText || '', disabled: this.approval() }),
      desktopAudio: new FormControl({ value: this.data?.desktopAudio || null, disabled: this.approval() }),
      mobileAudio: new FormControl({ value: this.data?.mobileAudio || null, disabled: this.approval() }),
      image: new FormControl({ value: this.data?.image || null, disabled: this.approval() }),
      approval: new FormControl({ value: this.data?.approval || 'Edit', disabled: this.approval() }),
      active: new FormControl({ value: this.data?.active || false, disabled: this.approval() }),
      version: new FormControl({ value: this.data?.version || 1, disabled: this.approval() }),
      changedBy: new FormControl({ value: this.data?.changedBy || '', disabled: this.approval() }),
      consumers: [normalizeOneRelation(this.data?.consumers)],
      subjects: [normalizeOneRelation(this.data?.subjects)],
    });
    this.approval.set(this.data?.approval === 'Dormant');
    this.key = this.data?.exerciseKind || '';
    if (this.data?.id) {
      this.isNewRecord.set(false);
      this.formGroup.get('consumers')?.disable();
    }
    this.buildURLs();
  }

  public ngOnInit(): void {
    this.formGroup.markAllAsTouched();
    this.loadRelationData();
    if (this.data?.markupText) {
      this.explanationContentHtml = this.data.markupText;

      this.viewContentHtml.set(
        this.sanitizer.bypassSecurityTrustHtml(this.data.markupText)
      );
    }
  }

  private loadRelationData(): void {
    const consumerQuery = { sort: 'name' };
    this.consumers$ = from(
      this.consumerService.ensureLoaded(consumerQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.consumerService.allByQuery(consumerQuery))
      )
    );

    const subjectQuery = { sort: 'name' };
    this.subjects$ = from(
      this.subjectService.ensureLoaded(subjectQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.subjectService.allByQuery(subjectQuery))
      )
    );
  }

  public ngAfterViewInit(): void {
    this.formGroup.markAllAsTouched();
  }

  public getConsumerError(): string {
    return getConsumerErrorText();
  }

  public getSubjectError(): string {
    return getSubjectErrorText();
  }

  private buildURLs(): void {
    const baseUrl = environment.isProd ? '/' : 'https://zav-stage.westermann.de/';

    if (this.data?.desktopAudio && this.data?.desktopAudio.name) {
      let desktopAudioUrl = baseUrl + 'media/audio/';
      Md5
        .hashStr(this.data?.desktopAudio.name)
        .split(/(.{8})/)
        .filter(O => O)
        .forEach((entry, idx) => {
          desktopAudioUrl += ((idx > 0) ? '/' : '') + entry.toUpperCase();
        });
      desktopAudioUrl += '.mp3';

      this.desktopAudioUrl.set(desktopAudioUrl);
    }

    if (this.data?.mobileAudio && this.data?.mobileAudio.name) {
      let mobileAudioUrl = baseUrl + 'media/audio/';
      Md5
        .hashStr(this.data?.mobileAudio.name)
        .split(/(.{8})/)
        .filter(O => O)
        .forEach((entry, idx) => {
          mobileAudioUrl += ((idx > 0) ? '/' : '') + entry.toUpperCase();
        });
      mobileAudioUrl += '.mp3';

      this.mobileAudioUrl.set(mobileAudioUrl);
    }

    if (this.data?.image && this.data?.image.name) {
      let imageUrl = baseUrl;
      if (this.data?.image.mediaType === 'Image') {
        imageUrl += 'media/image/';
      }

      if (this.data?.image.mediaType === 'Animation') {
        imageUrl += 'media/animation/';
      }

      Md5
        .hashStr(this.data?.image.name)
        .split(/(.{8})/)
        .filter(O => O)
        .forEach((entry, idx) => {
          imageUrl += ((idx > 0) ? '/' : '') + entry.toUpperCase();
        });

      if (this.data?.image.mediaType === 'Image') {
        imageUrl += '.png';
      }

      if (this.data?.image.mediaType === 'Animation') {
        imageUrl += '.gif';
      }

      this.imageUrl.set(imageUrl);
    }

    // Necessary, because the audio tag does not reload a new audio url by itself
    this.desktopAudioCtrl?.nativeElement?.load();
    this.mobileAudioCtrl?.nativeElement?.load();
  }

  public checkName(formControl: AbstractControl): string | null {
    return checkForNameErrors(formControl);
  }

  public getDesktopAudioName(): string {
    if (this.data?.desktopAudio && this.data?.desktopAudio.name) {
      return this.data?.desktopAudio.name;
    }
    return '';
  }

  public getMobileAudioName(): string {
    if (this.data?.mobileAudio && this.data?.mobileAudio.name) {
      return this.data?.mobileAudio.name;
    }
    return '';
  }

  public getImageName(): string {
    if (this.data?.image && this.data?.image.name) {
      return this.data?.image.name;
    }
    return '';
  }

  public openEditor(): void {
    const dialogRef = this.dialog.open(PixlEditorDialogComponent, {
      minWidth: '60vw',
      // width: '80vw',
      height: '70vh',
      data: {
        explanationContentHtml: this.explanationContentHtml,
        authToken: this.authToken,
        assetUrl: this.assetsUrl,
        mediaUrl: this.mediaUrl,
      }
    });
    dialogRef.afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((pixlEditorData: PixlEditorDialogModel) => {
        this.explanationContentHtml = pixlEditorData.explanationContentHtml;
        this.viewContentHtml.set(this.sanitizer.bypassSecurityTrustHtml(pixlEditorData.explanationContentHtml));
        const markupText = pixlEditorData.explanationContentHtml;
        const markdownText = this.turndownService.turndown(markupText);

        this.data!.markupText = markupText;
        this.data!.markdownText = markdownText;

        this.formGroup.patchValue({
          markupText,
          markdownText,
        });
      });
  }

  public openAudioChoice(desktop: boolean): void {
    if (desktop) {
      this.showMediumDialog('Audio', true);
    }
    else {
      this.showMediumDialog('Audio', false);
    }
  }

  public openImageChoice(): void {
    this.showMediumDialog('Image');
  }

  public showMediumDialog(mediaType: MediaType, desktopAudio?: boolean): void {
    const chooseMedium: ChooseMedium = {
      mediaType: mediaType,
      desktopAudio: desktopAudio,
      subjectId: this.data?.subjectId
    };

    const dialogRef = this.dialog.open(ChooseMediumComponent, {
      minWidth: '60vw',
      maxWidth: '80vw',
      minHeight: '530px',
      maxHeight: '530px',
      data: chooseMedium
    });

    dialogRef.afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((answer: AssetEntity) => {
        if (answer) {
          switch (mediaType) {
            case 'Image': {
              let imageModel: Medium;

              if (!this.data?.image) {
                imageModel = new Medium();
              }
              else {
                imageModel = this.data?.image;
              }
              imageModel.id = answer.id;
              imageModel.mediaType = mediaType;
              imageModel.name = answer.name;
              this.data!.image = imageModel;
              this.formGroup.patchValue({
                image: imageModel
              });
              break;
            }
            case 'Audio': {
              let audioModel: Medium;

              if (desktopAudio) {
                if (!this.data?.desktopAudio) {
                  audioModel = new Medium();
                }
                else {
                  audioModel = this.data?.desktopAudio;
                }

                audioModel.id = answer.id;
                audioModel.mediaType = mediaType;
                audioModel.name = answer.name;
                this.data!.desktopAudio = audioModel;
                this.formGroup.patchValue({
                  desktopAudio: audioModel
                });
              }
              else {
                if (!this.data?.mobileAudio) {
                  audioModel = new Medium();
                }
                else {
                  audioModel = this.data?.mobileAudio;
                }
                audioModel.id = answer.id;
                audioModel.mediaType = mediaType;
                audioModel.name = answer.name;
                this.data!.mobileAudio = audioModel;
                this.formGroup.patchValue({
                  mobileAudio: audioModel
                });
              }
            }
              break;
            default:
              break;
          }

          this.buildURLs();
        }
      });
  }

  public deleteMediaEntry(audio: boolean, desktopAudio: boolean): void {
    let id;
    let name;
    let headerText;

    if (audio) {
      headerText = 'Audio-Datei löschen';

      if (desktopAudio) {
        id = this.data?.desktopAudio?.id;
        name = this.data?.desktopAudio?.name;
      }
      else {
        id = this.data?.mobileAudio?.id;
        name = this.data?.mobileAudio?.name;
      }
    }

    if (!audio) {
      id = this.data?.image?.id;
      name = this.data?.image?.name;
      headerText = 'Bild-Datei löschen';
    }

    const dialogRef = this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          id: id,
          name: name,
          itemType: 'Datei',
          headerText: headerText,
          deleteFn: () => {
            if (audio) {
              if (desktopAudio) {
                this.data!.desktopAudio = null;
                this.formGroup.patchValue({
                  desktopAudio: null
                });
              }
              else {
                this.data!.mobileAudio = null;
                this.formGroup.patchValue({
                  mobileAudio: null
                });
              }
            }

            if (!audio) {
              this.data!.image = null;
              this.formGroup.patchValue({
                image: null
              });
            }
            dialogRef.close();
          }
        },
      });
  }

  public deleteEditorContent(): void {
    const headerText = 'Editor-Inhalt löschen';

    const dialogRef = this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          id: '',
          name: name,
          itemType: 'Datei',
          headerText: headerText,
          deleteFn: () => {
            // Setze das Signal auf einen leeren sicheren HTML-Wert
            this.explanationContentHtml = '';
            this.formGroup.patchValue({
              markdownText: '',
              markupText: ''
            });
            this.viewContentHtml.set(this.sanitizer.bypassSecurityTrustHtml(''));
            dialogRef.close();
          }
        },
      });
  }

  public consumerChanged(ev: string): void {
    if (ev) {
      this.consumerIsDeleted.set(false);
    }
  }

  public competencyChanged(ev: string): void {
    if (ev) {
      this.competencyIsDeleted.set(false);
    }
  }

  public onCancel(): void {
    this.dialogRef.close();
  }

  public onSave(): void {
    const subjectId = this.formGroup.value.subjects as string;
    const consumerId = this.formGroup.value.consumers as string;
    const exerciseKind = this.formGroup.value.exerciseKind as string;
    const formValue = this.formGroup.value;
    let splitExerciseKind: string [] = [];

    if (exerciseKind.includes('.')) {
      splitExerciseKind = exerciseKind.split('.');
    }
    else {
      splitExerciseKind[0] = exerciseKind;
      splitExerciseKind[1] = '';
    }

    const updatedFunctionalHelp = Object.assign(
      new FunctionalHelpEntity(),
      { id: this.data?.id },
      formValue,
      {
        subjectId: subjectId,
        consumerId: consumerId,
        exerciseKind: splitExerciseKind[0],
        exerciseSubKind: splitExerciseKind[1],
        consumers: formValue.consumers ? { id: formValue.consumers } : this.data?.consumers,
        subjects: formValue.subjects ? { id: formValue.subjects } : {},
      },
    );

    if (!this.formGroup.valid) {
      this.widgetService.setMessage({
        message: this.translocoService.translate('required_error'),
        messageTye: 'mtError',
      });
      return;
    }
    this.dialogRef.close(updatedFunctionalHelp);
  }
}
